import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Clock3, ExternalLink, RotateCcw, ShieldAlert } from 'lucide-react'
import { openTelegramOrBrowserLink } from '../utils/openTelegramOrBrowserLink'
import { openSupport } from '../services/supportService'

const STAGES = ['queued', 'buying', 'preparing', 'accept']
const REASONS = new Set([
  'price_rose', 'replacement_offered', 'not_found', 'seller_failed', 'declined',
  'tradelink_invalid', 'tradelink_outdated', 'profile_hidden', 'trade_ban',
  'user_not_found', 'support', 'other'
])
const TRADE_LINK_REASONS = new Set(['tradelink_invalid', 'tradelink_outdated', 'user_not_found'])
const BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT
const BOT_URL = BOT_USERNAME && /^[a-zA-Z0-9_]+$/.test(BOT_USERNAME) ? `https://t.me/${BOT_USERNAME}` : null

const isSteamOfferUrl = (value) => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname === 'steamcommunity.com'
  } catch {
    return false
  }
}

const SkinWithdrawProgress = ({ withdraw, onOpenSettings, onAction }) => {
  const { t } = useTranslation()
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0)

  useEffect(() => {
    const receivedAt = Date.now()
    setSecondsSinceUpdate(0)
    if (withdraw?.state !== 'active') return undefined
    const interval = setInterval(() => setSecondsSinceUpdate(Math.floor((Date.now() - receivedAt) / 1000)), 1000)
    return () => clearInterval(interval)
  }, [withdraw])

  if (!withdraw || !['active', 'failed'].includes(withdraw.state)) return null

  const failed = withdraw.state === 'failed'
  const total = Math.max(1, Number(withdraw.steps_total) || 4)
  const step = Math.min(total, Math.max(1, Number(withdraw.step) || 1))
  const stage = STAGES.includes(withdraw.stage) ? withdraw.stage : STAGES[step - 1]
  const reason = REASONS.has(withdraw.reason) ? withdraw.reason : 'other'
  const elapsed = Math.max(0, Number(withdraw.started_sec_ago) || 0) + secondsSinceUpdate
  const elapsedText = elapsed >= 3600
    ? t('inventory.withdrawProgress.hours', { count: Math.floor(elapsed / 3600) })
    : elapsed >= 60
      ? t('inventory.withdrawProgress.minutes', { count: Math.floor(elapsed / 60) })
      : t('inventory.withdrawProgress.seconds', { count: elapsed })
  const left = withdraw.accept_left_sec == null ? null : Math.max(0, Math.ceil(Number(withdraw.accept_left_sec) - secondsSinceUpdate))
  const offerAvailable = !failed && stage === 'accept' && withdraw.offer_url && isSteamOfferUrl(withdraw.offer_url)

  const handleAction = (event) => {
    event.stopPropagation()
    if (onAction) {
      onAction()
      return
    }
    if (failed) {
      if (TRADE_LINK_REASONS.has(reason)) onOpenSettings()
      else if (reason === 'support') openSupport()
      else if (reason === 'replacement_offered' && BOT_URL) openTelegramOrBrowserLink(BOT_URL)
    } else if (offerAvailable) {
      openTelegramOrBrowserLink(withdraw.offer_url)
    }
  }

  const actionKey = failed
    ? TRADE_LINK_REASONS.has(reason) ? 'updateTradeLink'
      : reason === 'support' ? 'contactSupport'
        : reason === 'replacement_offered' && BOT_URL ? 'openBot' : null
    : offerAvailable ? 'openTrade' : null

  return (
    <div className={`mt-4 rounded-2xl border p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25)] ${failed ? 'border-error/25 bg-error/5' : 'border-info/25 bg-info/5'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${failed ? 'bg-error/15 text-error' : 'bg-info/15 text-info'}`}>
            {failed ? <ShieldAlert size={19} /> : <Clock3 size={19} />}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white">{t(failed ? 'inventory.withdrawProgress.failed' : 'inventory.withdrawProgress.title')}</p>
            <p className="text-xs text-text-secondary">{t('inventory.withdrawProgress.stepOf', { step, total })}</p>
          </div>
        </div>
        {!failed && <span className="shrink-0 rounded-lg border border-info/20 bg-info/10 px-2 py-1 text-[11px] font-semibold text-info tabular-nums">{t('inventory.withdrawProgress.elapsed', { time: elapsedText })}</span>}
      </div>

      <div className="mt-4 flex gap-1.5" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={total} aria-label={t('inventory.withdrawProgress.title')}>
        {Array.from({ length: total }, (_, index) => (
          <span key={index} className={`h-1.5 flex-1 rounded-full ${failed && index === step - 1 ? 'bg-error' : index < step - 1 ? 'bg-info' : index === step - 1 && !failed ? 'bg-info' : 'bg-white/10'}`} />
        ))}
      </div>

      {failed ? (
        <div className="mt-4">
          <p className="text-sm font-semibold text-white">{t(`inventory.withdrawProgress.reasons.${reason}`)}</p>
          <p className="mt-1 text-xs leading-relaxed text-text-secondary">{t('inventory.withdrawProgress.returnedHint')}</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2.5">
          {Array.from({ length: total }, (_, index) => {
            const current = index + 1 === step
            const complete = index + 1 < step
            const key = current ? stage : STAGES[index]
            return (
              <div key={index} className={`flex items-center gap-3 ${current ? 'text-white' : complete ? 'text-info' : 'text-text-secondary/60'}`}>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold tabular-nums ${current ? 'border-info bg-info/15 text-info' : complete ? 'border-info/40 bg-info/10' : 'border-white/10'}`}>
                  {complete ? <Check size={13} strokeWidth={3} /> : index + 1}
                </span>
                <span className={`text-xs ${current ? 'font-semibold' : ''}`}>{t(`inventory.withdrawProgress.stages.${key || 'unknown'}`)}</span>
                {current && <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-wider text-info">{t('inventory.withdrawProgress.inProgress')}</span>}
              </div>
            )
          })}
        </div>
      )}

      {!failed && stage === 'accept' && (
        <div className="mt-4 rounded-xl border border-white/10 bg-surface px-3 py-2.5 text-xs text-text-secondary">
          {left === null ? t('inventory.withdrawProgress.acceptHint') : left === 0
            ? t('inventory.withdrawProgress.checkingTrade')
            : <span className="flex items-center justify-between gap-2"><span>{t('inventory.withdrawProgress.timeLeft')}</span><strong className="text-white tabular-nums">{String(Math.floor(left / 60)).padStart(2, '0')}:{String(left % 60).padStart(2, '0')}</strong></span>}
        </div>
      )}
      {actionKey && (
        <button type="button" onClick={handleAction} className={`mt-3 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all active:scale-[0.98] ${failed ? 'border border-white/15 bg-white/5 text-white hover:bg-white/10' : 'bg-info text-dark-primary hover:opacity-90'}`}>
          {failed ? <RotateCcw size={16} /> : <ExternalLink size={16} />}
          {t(`inventory.withdrawProgress.${actionKey}`)}
        </button>
      )}
    </div>
  )
}

export default SkinWithdrawProgress
